import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  CircularProgress,
  IconButton,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentIcon from '@mui/icons-material/Payment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import useIsMobile from '../hooks/useIsMobile';

function PaymentManagement() {
  const { vin } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useIsMobile();
  
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState({});
  
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  
  const [newPlan, setNewPlan] = useState({
    customer_name: '',
    total_amount: '',
    down_payment: '',
    interest_rate: '',
    term_months: ''
  });
  
  const [newPayment, setNewPayment] = useState({
    amount: '',
    date_paid: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    notes: ''
  });

  useEffect(() => {
    fetchVehicleInfo();
    fetchPaymentPlan();
  }, [vin]);

  const fetchVehicleInfo = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory?username=${username}`);
      if (response.ok) {
        const data = await response.json();
        const vehicle = data.inventory.find(v => v.vin === vin);
        if (vehicle) {
          setVehicleInfo(vehicle);
        }
      }
    } catch (error) {
      console.error('Error fetching vehicle info:', error);
    }
  };

  const fetchPaymentPlan = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/${vin}?username=${username}`);
      
      if (response.ok) {
        const data = await response.json();
        setPaymentPlan(data);
      } else if (response.status === 404) {
        setPaymentPlan(null);
      } else {
        throw new Error('Failed to fetch payment plan');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async () => {
    try {
      const username = localStorage.getItem('username');
      const planData = {
        ...newPlan,
        vin,
        username,
        vehicle_make: vehicleInfo.make || '',
        vehicle_model: vehicleInfo.model || ''
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(planData)
      });

      if (response.ok) {
        setCreatePlanOpen(false);
        setNewPlan({
          customer_name: '',
          total_amount: '',
          down_payment: '',
          interest_rate: '',
          term_months: ''
        });
        fetchPaymentPlan();
      } else {
        const errorData = await response.json();
        setError(errorData.error);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleAddPayment = async () => {
    try {
      const username = localStorage.getItem('username');
      const paymentData = {
        ...newPayment,
        username
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/${vin}/payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentData)
      });

      if (response.ok) {
        setAddPaymentOpen(false);
        setNewPayment({
          amount: '',
          date_paid: new Date().toISOString().split('T')[0],
          payment_method: 'cash',
          notes: ''
        });
        fetchPaymentPlan();
      } else {
        const errorData = await response.json();
        setError(errorData.error);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeletePayment = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/payments/${vin}/payment/${paymentToDelete}?username=${username}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        setDeleteConfirmOpen(false);
        setPaymentToDelete(null);
        fetchPaymentPlan();
      } else {
        const errorData = await response.json();
        setError(errorData.error);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const calculatePaymentStatus = () => {
    if (!paymentPlan) return {};
    
    const totalPaid = paymentPlan.payments.reduce((sum, payment) => sum + payment.amount, 0);
    const totalWithDownPayment = totalPaid + paymentPlan.down_payment;
    const remaining = paymentPlan.total_amount - totalWithDownPayment;
    const progress = (totalWithDownPayment / paymentPlan.total_amount) * 100;
    
    return {
      totalPaid,
      totalWithDownPayment,
      remaining: Math.max(0, remaining),
      progress: Math.min(100, progress),
      isComplete: remaining <= 0
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const glassmorphismStyle = {
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    boxShadow: theme.shadows[3],
    borderRadius: 2,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={50} />
      </Box>
    );
  }

  const paymentStatus = calculatePaymentStatus();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={() => navigate(`/view-deal/${vin}`)}
              sx={{
                mr: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
              Payment Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ pl: { xs: 0, sm: `calc(40px + ${theme.spacing(1.5)})` } }}>
            {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model} - VIN: {vin}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {!paymentPlan ? (
          <Paper elevation={0} sx={{ ...glassmorphismStyle, p: 4, textAlign: 'center' }}>
            <PaymentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Payment Plan Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a payment plan to start tracking payments for this vehicle.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreatePlanOpen(true)}
              size="large"
            >
              Create Payment Plan
            </Button>
          </Paper>
        ) : (
          <>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={{ ...glassmorphismStyle, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <MonetizationOnIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      Total Amount
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {formatCurrency(paymentPlan.total_amount)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={{ ...glassmorphismStyle, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      Total Paid
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {formatCurrency(paymentStatus.totalWithDownPayment)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={{ ...glassmorphismStyle, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <TrendingUpIcon sx={{ fontSize: 40, color: paymentStatus.remaining > 0 ? 'warning.main' : 'success.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      Remaining
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: paymentStatus.remaining > 0 ? 'warning.main' : 'success.main' }}>
                      {formatCurrency(paymentStatus.remaining)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ ...glassmorphismStyle, p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1 }} />
                    Plan Details
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Customer:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{paymentPlan.customer_name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Down Payment:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatCurrency(paymentPlan.down_payment)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Amount Financed:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatCurrency(paymentPlan.amount_financed)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Interest Rate:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{paymentPlan.interest_rate}%</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Term:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{paymentPlan.term_months} months</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Monthly Payment:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{formatCurrency(paymentPlan.monthly_payment)}</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ ...glassmorphismStyle, p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Payment Progress
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Progress</Typography>
                      <Typography variant="body2">{paymentStatus.progress.toFixed(1)}%</Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        height: 10,
                        borderRadius: 5,
                        bgcolor: alpha(theme.palette.grey[500], 0.3),
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: `${paymentStatus.progress}%`,
                          height: '100%',
                          bgcolor: paymentStatus.isComplete ? 'success.main' : 'primary.main',
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </Box>
                  </Box>
                  <Chip
                    label={paymentStatus.isComplete ? 'PAID IN FULL' : 'IN PROGRESS'}
                    color={paymentStatus.isComplete ? 'success' : 'primary'}
                    variant="filled"
                    sx={{ fontWeight: 'bold' }}
                  />
                </Paper>
              </Grid>
            </Grid>

            <Paper elevation={0} sx={{ ...glassmorphismStyle, mb: 3 }}>
              <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1 }} />
                    Payment History
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAddPaymentOpen(true)}
                    disabled={paymentStatus.isComplete}
                  >
                    Add Payment
                  </Button>
                </Box>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: alpha(theme.palette.grey[500], 0.1) } }}>
                      <TableCell>Date Paid</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Method</TableCell>
                      {!isMobile && <TableCell>Notes</TableCell>}
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                      <TableCell>{paymentPlan.created_date}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(paymentPlan.down_payment)}
                      </TableCell>
                      <TableCell>
                        <Chip label="Down Payment" size="small" color="success" />
                      </TableCell>
                      {!isMobile && <TableCell>Initial down payment</TableCell>}
                      <TableCell align="center">-</TableCell>
                    </TableRow>
                    
                    {paymentPlan.payments.map((payment, index) => (
                      <TableRow key={payment.payment_id} sx={{ '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.grey[500], 0.05) } }}>
                        <TableCell>{payment.date_paid}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell>
                          <Chip label={payment.payment_method.toUpperCase()} size="small" />
                        </TableCell>
                        {!isMobile && <TableCell>{payment.notes || '-'}</TableCell>}
                        <TableCell align="center">
                          <IconButton
                            onClick={() => {
                              setPaymentToDelete(payment.payment_id);
                              setDeleteConfirmOpen(true);
                            }}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {paymentPlan.payments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isMobile ? 4 : 5} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          No payments recorded yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}

        <Modal open={createPlanOpen} onClose={() => setCreatePlanOpen(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '500px' },
              ...glassmorphismStyle,
              p: 4
            }}
          >
            <Typography variant="h6" gutterBottom>
              Create Payment Plan
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Stack spacing={3}>
              <TextField
                label="Customer Name"
                value={newPlan.customer_name}
                onChange={(e) => setNewPlan({ ...newPlan, customer_name: e.target.value })}
                fullWidth
                required
              />
              
              <TextField
                label="Total Amount"
                type="number"
                value={newPlan.total_amount}
                onChange={(e) => setNewPlan({ ...newPlan, total_amount: e.target.value })}
                InputProps={{ startAdornment: <Box component="span" sx={{ mr: 0.5 }}>$</Box> }}
                fullWidth
                required
              />
              
              <TextField
                label="Down Payment"
                type="number"
                value={newPlan.down_payment}
                onChange={(e) => setNewPlan({ ...newPlan, down_payment: e.target.value })}
                InputProps={{ startAdornment: <Box component="span" sx={{ mr: 0.5 }}>$</Box> }}
                fullWidth
                required
              />
              
              <TextField
                label="Interest Rate"
                type="number"
                value={newPlan.interest_rate}
                onChange={(e) => setNewPlan({ ...newPlan, interest_rate: e.target.value })}
                InputProps={{ endAdornment: <Box component="span" sx={{ ml: 0.5 }}>%</Box> }}
                fullWidth
                required
              />
              
              <TextField
                label="Term (Months)"
                type="number"
                value={newPlan.term_months}
                onChange={(e) => setNewPlan({ ...newPlan, term_months: e.target.value })}
                fullWidth
                required
              />
              
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={() => setCreatePlanOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreatePlan}
                  disabled={!newPlan.customer_name || !newPlan.total_amount || !newPlan.down_payment || !newPlan.interest_rate || !newPlan.term_months}
                >
                  Create Plan
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal>

        <Modal open={addPaymentOpen} onClose={() => setAddPaymentOpen(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '400px' },
              ...glassmorphismStyle,
              p: 4
            }}
          >
            <Typography variant="h6" gutterBottom>
              Add Payment
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Stack spacing={3}>
              <TextField
                label="Payment Amount"
                type="number"
                value={newPayment.amount}
                onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                InputProps={{ startAdornment: <Box component="span" sx={{ mr: 0.5 }}>$</Box> }}
                fullWidth
                required
              />
              
              <TextField
                label="Date Paid"
                type="date"
                value={newPayment.date_paid}
                onChange={(e) => setNewPayment({ ...newPayment, date_paid: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
              />
              
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={newPayment.payment_method}
                  onChange={(e) => setNewPayment({ ...newPayment, payment_method: e.target.value })}
                  label="Payment Method"
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                label="Notes (Optional)"
                value={newPayment.notes}
                onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
              
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={() => setAddPaymentOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddPayment}
                  disabled={!newPayment.amount || !newPayment.date_paid}
                >
                  Add Payment
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal>

        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this payment record? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleDeletePayment} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </motion.div>
  );
}

export default PaymentManagement; 